/**
 * Estado do calendario (spec 9.4): intervalo visivel, navegacao e reagendamento.
 *
 * Todas as visoes derivam de UM intervalo `[from, to]` pedido ao servidor. A
 * alternativa — baixar tudo e filtrar no cliente — funciona com 150 tarefas e
 * para de funcionar em silencio quando a base cresce; o `from`/`to` da API
 * existe justamente para isso.
 */

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  addDaysInZone,
  addMonthsInZone,
  endOfDayInZone,
  isoDateInZone,
  partsInZone,
  startOfDayInZone,
  startOfMonthInZone,
  zonedPartsToUtc,
  type Task,
} from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { useToast } from '../../components/ui/Toast.js';
import { taskKeys, useTaskList, useUpdateTask } from '../tasks/useTasks.js';

export type CalendarViewId = 'agenda' | 'year' | 'month' | 'week' | 'three' | 'day';

export interface CalendarViewDef {
  id: CalendarViewId;
  label: string;
  /**
   * `grid` = colunas de dia com faixa de horas (Dia, 3 Dias, Semana).
   * As tres sao a MESMA visao com contagem de colunas diferente — a spec 12
   * as lista como arquivos separados, mas triplicar o componente por causa de
   * um inteiro criaria tres lugares para o mesmo bug.
   *
   * `year` = doze mini-meses (como o "Ano" do TickTick): so marca os dias com
   * tarefa; o clique leva ao dia.
   */
  kind: 'grid' | 'month' | 'agenda' | 'year';
  days?: number;
}

/**
 * Ordem do seletor no wireframe da spec 9.4: [Lista][Mes][Semana][3 Dias][Dia].
 * O Ano entrou antes do Mes para o menu seguir do periodo maior ao menor.
 */
export const CALENDAR_VIEWS: readonly CalendarViewDef[] = [
  { id: 'agenda', label: 'Lista', kind: 'agenda' },
  { id: 'year', label: 'Ano', kind: 'year' },
  { id: 'month', label: 'Mês', kind: 'month' },
  { id: 'week', label: 'Semana', kind: 'grid', days: 7 },
  { id: 'three', label: '3 Dias', kind: 'grid', days: 3 },
  { id: 'day', label: 'Dia', kind: 'grid', days: 1 },
] as const;

export function viewDef(id: CalendarViewId): CalendarViewDef {
  return CALENDAR_VIEWS.find((v) => v.id === id) ?? CALENDAR_VIEWS[0]!;
}

export interface CalendarRange {
  from: number;
  to: number;
  /** Inicio de cada dia civil exibido, em ordem. */
  days: number[];
  /**
   * Teto de linhas pedido ao servidor. So o ano define: o padrao da API
   * (`TASK_PAGE_SIZE`, 300) cobre com folga um mes, mas cortaria um ano cheio
   * e a grade marcaria como vazios dias que tem tarefa.
   */
  limit?: number;
}

/** Maximo aceito pela API (`taskQuerySchema.limit`). */
const YEAR_LIMIT = 2000;

/** Horizonte da visao em lista. 30 dias cobre o planejamento sem virar scroll infinito. */
const AGENDA_DAYS = 30;

/** A grade do mes tem 6 semanas fixas — 42 celulas — para nao "pular" de altura. */
const MONTH_CELLS = 42;

/**
 * Domingo 00:00 da semana que contem `epochMs` — o inicio da semana NA GRADE.
 *
 * Domingo, como na referencia do TickTick e no calendario de parede brasileiro
 * (pedido do usuario em 13/10/2026, HANDOFF §8ab). A spec 9.4 desenhava a
 * semana na segunda; divergencia registrada.
 *
 * Fica aqui, e nao no `startOfWeekInZone` do shared: aquele segue o RFC 5545
 * (WKST=MO) e serve a recorrencia e as estatisticas de foco, que nao devem
 * mudar porque a grade mudou de primeira coluna.
 */
export function inicioDaSemanaNaGrade(timeZone: string, epochMs: number): number {
  const dia = startOfDayInZone(timeZone, epochMs);
  return addDaysInZone(timeZone, dia, -partsInZone(timeZone, dia).weekday);
}

function sequenceOfDays(timeZone: string, start: number, count: number): number[] {
  const days: number[] = [];
  for (let i = 0; i < count; i++) days.push(addDaysInZone(timeZone, start, i));
  return days;
}

export function calendarRange(
  view: CalendarViewId,
  anchor: number,
  timeZone: string,
): CalendarRange {
  const def = viewDef(view);

  if (def.kind === 'year') {
    // UMA consulta para o ano inteiro, e nao doze: os mini-meses sao uma tela
    // so, e doze requisicoes em paralelo para pintar pontinhos seriam doze
    // idas ao D1 sem ganho nenhum.
    const { year } = partsInZone(timeZone, anchor);
    const start = zonedPartsToUtc(timeZone, { year, month: 1, day: 1 });
    const fim = zonedPartsToUtc(timeZone, { year, month: 12, day: 31 });
    const total = Math.round((fim - start) / 86_400_000) + 1;
    const days = sequenceOfDays(timeZone, start, total);
    return { from: start, to: endOfDayInZone(timeZone, fim), days, limit: YEAR_LIMIT };
  }

  if (def.kind === 'month') {
    // A grade comeca no domingo anterior ao dia 1, entao mostra dias do mes
    // vizinho. Isso e proposital: uma semana partida ao meio esconderia
    // compromissos que caem na virada.
    const start = inicioDaSemanaNaGrade(timeZone, startOfMonthInZone(timeZone, anchor));
    const days = sequenceOfDays(timeZone, start, MONTH_CELLS);
    return { from: start, to: endOfDayInZone(timeZone, days[MONTH_CELLS - 1]!), days };
  }

  if (def.kind === 'agenda') {
    const start = startOfDayInZone(timeZone, anchor);
    const days = sequenceOfDays(timeZone, start, AGENDA_DAYS);
    return { from: start, to: endOfDayInZone(timeZone, days[AGENDA_DAYS - 1]!), days };
  }

  const count = def.days ?? 1;
  // A semana ancora no domingo; Dia e 3 Dias comecam no proprio dia ancorado.
  const start =
    count === 7 ? inicioDaSemanaNaGrade(timeZone, anchor) : startOfDayInZone(timeZone, anchor);
  const days = sequenceOfDays(timeZone, start, count);

  return { from: start, to: endOfDayInZone(timeZone, days[count - 1]!), days };
}

/** Avanca ou recua um "periodo" da visao atual. */
export function shiftAnchor(
  view: CalendarViewId,
  anchor: number,
  timeZone: string,
  direction: 1 | -1,
): number {
  const def = viewDef(view);

  if (def.kind === 'year') return addMonthsInZone(timeZone, anchor, direction * 12);
  if (def.kind === 'month') return addMonthsInZone(timeZone, anchor, direction);
  if (def.kind === 'agenda') return addDaysInZone(timeZone, anchor, direction * AGENDA_DAYS);

  return addDaysInZone(timeZone, anchor, direction * (def.days ?? 1));
}

// ------------------------------------------------------- alvos de soltura

/**
 * Identificador de um alvo de drop.
 *
 * O dnd-kit so transporta um id de string, entao o destino precisa ser
 * codificado nele. Um separador explicito (`|`) mais um parser dedicado evita
 * o `id.split('-')` ingenuo — que quebraria na primeira data, porque
 * 'YYYY-MM-DD' ja contem hifens.
 */
export type DropTarget =
  | { kind: 'slot'; date: string; hour: number }
  | { kind: 'allday'; date: string }
  | { kind: 'day'; date: string };

export function dropTargetId(target: DropTarget): string {
  return target.kind === 'slot'
    ? `slot|${target.date}|${target.hour}`
    : `${target.kind}|${target.date}`;
}

export function parseDropTarget(id: string): DropTarget | null {
  const [kind, date, hour] = id.split('|');
  if (!date) return null;

  if (kind === 'slot') {
    const parsed = Number(hour);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 23) return null;
    return { kind: 'slot', date, hour: parsed };
  }

  if (kind === 'allday') return { kind: 'allday', date };
  if (kind === 'day') return { kind: 'day', date };

  return null;
}

/** 'YYYY-MM-DD' -> partes de calendario, sem passar por `new Date`. */
function parseIsoDate(date: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;

  const [, year, month, day] = match;
  return { year: Number(year), month: Number(month), day: Number(day) };
}

/**
 * Novo vencimento de uma tarefa solta num alvo do calendario.
 *
 * Funcao pura, fora do componente, porque e aqui que mora toda a aritmetica de
 * data do arrastar — e aritmetica de data e onde os bugs se escondem. Dentro
 * de um `onDragEnd` ela so seria alcancavel simulando um arrasto no jsdom.
 *
 * Devolve `null` quando nao ha mudanca a fazer, para o chamador nao disparar
 * uma escrita (e um toast de "Reagendada") a toa.
 */
export function resolveDropDueAt(
  task: Task,
  target: DropTarget,
  timeZone: string,
): { dueAt: number; allDay: boolean } | null {
  const data = parseIsoDate(target.date);
  if (!data) return null;

  const atual = task.dueAt === null ? null : partsInZone(timeZone, task.dueAt);

  const resultado = (() => {
    if (target.kind === 'allday') {
      // Sem hora marcada, o instante ancora no fim do dia — mesma convencao da
      // entrada rapida, para a tarefa ordenar por ultimo dentro do dia.
      return {
        dueAt: endOfDayInZone(timeZone, zonedPartsToUtc(timeZone, data)),
        allDay: true,
      };
    }

    if (target.kind === 'slot') {
      // Troca a hora, mas preserva os minutos: arrastar uma tarefa das 14:30
      // para a faixa das 9h deve dar 09:30, nao 09:00.
      return {
        dueAt: zonedPartsToUtc(timeZone, {
          ...data,
          hour: target.hour,
          minute: atual?.minute ?? 0,
        }),
        allDay: false,
      };
    }

    // Grade do mes: troca a data e mantem a hora de parede intacta.
    if (task.allDay || atual === null) {
      return {
        dueAt: endOfDayInZone(timeZone, zonedPartsToUtc(timeZone, data)),
        allDay: true,
      };
    }

    return {
      dueAt: zonedPartsToUtc(timeZone, { ...data, hour: atual.hour, minute: atual.minute }),
      allDay: false,
    };
  })();

  if (resultado.dueAt === task.dueAt && resultado.allDay === task.allDay) return null;
  return resultado;
}

/** Indexa as tarefas pelo dia civil a que pertencem. */
export function groupByDay(tasks: Task[], timeZone: string): Map<string, Task[]> {
  const byDay = new Map<string, Task[]>();

  for (const task of tasks) {
    if (task.dueAt === null) continue;

    const key = isoDateInZone(timeZone, task.dueAt);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(task);
    else byDay.set(key, [task]);
  }

  return byDay;
}

export function useCalendarTasks(range: CalendarRange) {
  const query = useTaskList({
    view: 'list',
    from: range.from,
    to: range.to,
    // As concluidas continuam visiveis no calendario: elas aconteceram naquele
    // dia. Esconde-las faria a grade mentir sobre o que o dia conteve.
    includeDone: true,
    ...(range.limit !== undefined ? { limit: range.limit } : {}),
  });

  return {
    tasks: useMemo(() => query.data?.tasks ?? [], [query.data?.tasks]),
    now: query.data?.now ?? Date.now(),
    // O ano pede ate `YEAR_LIMIT` linhas; passando disso o servidor corta e
    // avisa. Sem repassar o aviso, a grade marcaria como livres dias que tem
    // tarefa — e o ano existe justamente para responder "quando estou livre".
    hasMore: query.data?.hasMore ?? false,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Reagenda uma tarefa — o efeito de arrastar um bloco na grade (spec 9.4).
 *
 * Desfazivel por 8 s, como toda mudanca de estado (spec 10.3): errar o alvo de
 * uma hora arrastando e facil, e sem a volta o usuario teria de lembrar o
 * horario anterior para consertar.
 */
export function useReschedule() {
  const updateTask = useUpdateTask();
  const { showToast } = useToast();
  const qc = useQueryClient();

  return (task: Task, dueAt: number, allDay: boolean) => {
    if (task.dueAt === dueAt && task.allDay === allDay) return;

    const anterior = { dueAt: task.dueAt, allDay: task.allDay };
    updateTask.mutate({ id: task.id, patch: { dueAt, allDay } });

    showToast({
      message: 'Reagendada',
      action: {
        label: 'Desfazer',
        onAction: () => {
          void (async () => {
            await api.tasks.update(task.id, anterior);
            void qc.invalidateQueries({ queryKey: taskKeys.all });
          })();
        },
      },
    });
  };
}
